"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TextInput = TextInput;
exports.PasswordInput = PasswordInput;
exports.Textarea = Textarea;
exports.Label = Label;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const cn_1 = require("../lib/cn");
const icon_1 = require("./icon");
const tooltip_1 = require("./tooltip");
/**
 * Text entry. ⚠️ The class string is the mockup's, verbatim.
 *
 * ⚠️ **`border-input bg-transparent` means the border is the input's only identification** —
 * there is no fill to distinguish it from the card behind it. The contrast audit measures that
 * border at 1.26:1 against a bar of 3, in every one of the eight themes, and the waiver in
 * `__tests__/contrast.test.ts` explains why it is escalated rather than corrected here. If a
 * darker `--input` token ever lands, this component needs no change at all — which is the
 * point of naming a token instead of a colour.
 */
const INPUT = 'flex h-8 w-full min-w-0 rounded-md border border-input bg-transparent px-2.5 py-1 text-sm ' +
    'shadow-xs transition-[color,box-shadow] outline-none placeholder:text-muted-foreground ' +
    'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 ' +
    'disabled:cursor-not-allowed disabled:opacity-50';
/* ⚠️ On all three inputs `title` is the kit's tooltip, never the browser's — `Button` carries the
 * argument. Bound to the input itself, so a `w-full` field keeps its width. */
function TextInput({ className, title, ...rest }) {
    const tip = (0, tooltip_1.useTooltip)(title);
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("input", { className: (0, cn_1.cn)(INPUT, className), ...rest, ...tip.triggerProps(rest) }), tip.bubble] }));
}
/**
 * A password field with the eye that shows what was typed. Asked for after a UAT afternoon spent
 * failing to sign in with a password that was, in the end, right (17 Oct 2026) — the mockup draws
 * no such control, so the design system's input is the whole of its look.
 *
 * ⚠️⚠️ **THE EYE IS `type="button"`, AND WITHOUT IT LOOKING AT THE PASSWORD SENDS IT.** A
 * `<button>` with no type is a submit button: a click on the eye would submit the form, and — as
 * the first submit button, ahead of the form's real one — it is also the one Enter would press.
 *
 * ⚠️ **The two labels are props**, because a component here carries no strings of its own.
 */
function PasswordInput({ className, showLabel, hideLabel, title, ...rest }) {
    const [visible, setVisible] = (0, react_1.useState)(false);
    const tip = (0, tooltip_1.useTooltip)(title);
    return ((0, jsx_runtime_1.jsxs)("div", { className: "relative w-full", children: [(0, jsx_runtime_1.jsx)("input", { type: visible ? 'text' : 'password', className: (0, cn_1.cn)(INPUT, 'pr-8', className), ...rest, ...tip.triggerProps(rest) }), tip.bubble, (0, jsx_runtime_1.jsx)("button", { type: "button", "aria-label": visible ? hideLabel : showLabel, "aria-pressed": visible, className: "text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 absolute inset-y-0 right-0 flex w-8 items-center justify-center rounded-r-md outline-none focus-visible:ring-[3px]", onClick: () => {
                    setVisible((shown) => !shown);
                }, children: (0, jsx_runtime_1.jsx)(icon_1.Icon, { name: visible ? 'eye-off' : 'eye', size: 14 }) })] }));
}
/**
 * ⚠️ **`ref` is declared, because `TextareaHTMLAttributes` does not include it.** React 19 passes a
 * ref to a function component as an ordinary prop — no `forwardRef` — but the attribute type it is
 * spread into knows nothing about that, so a caller that needs the element (C17's mention picker
 * reads `selectionStart`) gets *"Property 'ref' does not exist"*. Adding it here is the fix; casting
 * at the call site would be the same code with the type lie moved.
 */
function Textarea({ className, title, ...rest }) {
    const tip = (0, tooltip_1.useTooltip)(title);
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("textarea", { className: (0, cn_1.cn)(INPUT, 'field-sizing-content h-auto min-h-16 py-2', className), ...rest, ...tip.triggerProps(rest) }), tip.bubble] }));
}
function Label({ className, children, ...rest }) {
    return ((0, jsx_runtime_1.jsx)("label", { className: (0, cn_1.cn)('flex items-center gap-2 text-[12.5px] font-medium leading-none select-none', className), ...rest, children: children }));
}
//# sourceMappingURL=field.js.map